import { Suspense, useState } from 'react';

import {
    TFormalOffers,
    TRBLSchedule
} from '@helpers/FormalOffers/formalOffers.types';
import { getIsRBLOffer } from '@helpers/FormalOffers/getIsRBLOffer';
import { GetFormalOffersResponse } from '@iwoca/lapi-client/edge';
import { Skeleton } from '@ui';
import { FormattedMessage, FormattedDate } from 'react-intl';

import messages from './CustomiseOffer.messages';
import { useGetCustomiseOfferData } from './useGetCustomiseOfferData';
import { ChooseLoanAmount } from '../../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import { MoneyNoDecimals } from '../../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { Tooltip } from '../../../../ui/tooltip/Tooltip';
import { FormalOffer } from '../../../MyOffers.types';
import { RBLOfferConfiguration } from '../../offerConfigurationView/RBLOfferConfigurationView/RBLOfferConfigurationView';
import { OfferChoiceButtons } from '../offerChoiceButtons/OfferChoiceButtons';
import { SelectOfferType } from '../selectOfferType/selectOfferType';

type CustomiseOfferProps = {
    drawdownAmount: number;
    updateDrawdownAmount: (drawdownAmount: number) => void;
    selectedOffer: FormalOffer | null;
    updateSelectedOffer: (offer: FormalOffer | null) => void;
    formalOffers: NonNullable<GetFormalOffersResponse['data']>;
    updateRblSchedule: (next: TRBLSchedule) => void;
    minimumDrawdownAmount: number;
};

export const CustomiseOffer = ({
    drawdownAmount,
    updateDrawdownAmount,
    selectedOffer,
    updateSelectedOffer,
    formalOffers,
    updateRblSchedule,
    minimumDrawdownAmount
}: CustomiseOfferProps) => {
    const {
        maxAvailableCash,
        shouldShowOfferTypeSelection,
        hasInterestOnlyChoice,
        initialOfferChoices
    } = useGetCustomiseOfferData(formalOffers.formal_offers);

    const [offerChoices, setOfferChoices] = useState<TFormalOffers | []>(
        initialOfferChoices
    );

    return (
        <>
            <ChooseLoanAmount
                updateDrawdownAmount={updateDrawdownAmount}
                drawdownAmount={drawdownAmount}
                minimumDrawdownAmount={minimumDrawdownAmount}
                maximumDrawdownAmount={maxAvailableCash}
                inputTooltip={
                    <Tooltip
                        data-action-name="Input tooltip"
                        message={
                            <FormattedMessage
                                {...messages.inputTooltip}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            value={maxAvailableCash}
                                        />
                                    ),
                                    date: (
                                        <FormattedDate
                                            value={
                                                selectedOffer
                                                    ? new Date(
                                                          selectedOffer.timeout!
                                                      ).toISOString()
                                                    : new Date()
                                            }
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        }
                    />
                }
            />

            {shouldShowOfferTypeSelection && (
                <SelectOfferType
                    setOfferChoices={setOfferChoices}
                    drawdownAmount={drawdownAmount}
                    updateSelectedOffer={updateSelectedOffer}
                />
            )}

            {selectedOffer && getIsRBLOffer(selectedOffer) ? (
                <RBLOfferConfiguration
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                    updateRblSchedule={updateRblSchedule}
                />
            ) : (
                formalOffers.formal_offers.length > 1 &&
                offerChoices.length > 0 && (
                    <Suspense fallback={<StackOfSkeletons />}>
                        <OfferChoiceButtons
                            selectedOffer={selectedOffer}
                            updateSelectedOffer={updateSelectedOffer}
                            formalOffers={offerChoices}
                            variationTrees={
                                formalOffers.formal_offer_variation_relationship_trees
                            }
                            drawdownAmount={drawdownAmount}
                            hasInterestOnlyChoice={hasInterestOnlyChoice}
                        />
                    </Suspense>
                )
            )}
        </>
    );
};

const StackOfSkeletons = () => {
    return (
        <div className="space-y-xl">
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
        </div>
    );
};
